import { saveAs } from "file-saver";
import reqNotaFiscal from "../../../services/reqCobranca/reqNotaFiscal";
import reqVenda from "../../../services/reqMultiTenant/reqVenda";
import functions from "../../../util/functions";
import LibMask from "../../../util/mask";
import { bytesDeBase64 } from "../../boleto/remessa/hooks";

const AMBIENTES = { HOMOLOGACAO: "Homologação", PRODUCAO: "Produção" };

const DESFECHOS = {
	CANCELADA: { titulo: "Cancelado", tom: "cinza" },
	DENEGADA: { titulo: "Denegado", tom: "vermelho" },
	REJEITADA: { titulo: "Rejeitado", tom: "vermelho" },
	ERRO: { titulo: "Falha na emissão", tom: "vermelho" },
};

function eventosDe(nota) {
	const retorno = [nota.cstat, nota.motivo].filter(Boolean).join(" · ");
	const desfecho = DESFECHOS[nota.status];

	return [
		{ titulo: "Emissão iniciada", data: nota.data_cadastro, tom: "neutro" },
		nota.data_emissao && { titulo: "XML gerado e assinado", data: nota.data_emissao, tom: "neutro" },
		nota.data_tentativa && {
			titulo: "Enviado à SEFAZ",
			data: nota.data_tentativa,
			tom: "neutro",
			detalhe: `${nota.tentativas} tentativa(s)`,
		},
		nota.data_autorizacao && {
			titulo: "Autorizado",
			data: nota.data_autorizacao,
			tom: "verde",
			detalhe: `Protocolo ${nota.protocolo}`,
		},
		desfecho && { ...desfecho, data: nota.data_movimentacao, detalhe: retorno },
	].filter(Boolean).reverse();
}

function documentoDe(nota) {
	return {
		...nota,
		ambiente: AMBIENTES[nota.ambiente] ?? nota.ambiente,
		eventos: eventosDe(nota),
	};
}

const ehTipo = (complemento, tipo) => String(complemento.tipo ?? "").toUpperCase() === tipo;

function linhasDaVenda(itens, complementos) {
	return itens.flatMap((item) => {
		const doItem = complementos.filter((c) => c.id_venda_item === item.id_venda_item);
		const descricao = item.nome || item.descricao;

		if (doItem.length === 0) return [{ tipo: "item", descricao, quantidade: item.qtde, total: item.valor }];

		const grade = doItem.some((c) => ehTipo(c, "GRADE"));
		const totalComplementos = doItem.reduce((soma, c) => soma + Number(c.valor), 0);

		return [
			{
				tipo: "pai",
				descricao,
				quantidade: `${item.qtde}x`,
				total: grade ? totalComplementos * Number(item.qtde) : Number(item.valor) + totalComplementos,
			},
			...doItem.map((c) => ({
				tipo: "complemento",
				descricao: c.nome,
				quantidade: c.qtde,
				total: ehTipo(c, "SUBITEM") && Number(c.valor) === 0 ? null : c.valor,
			})),
		];
	});
}

function vendaDe({ venda, conta_entrada = [], venda_item = [], venda_complementos = [] }) {
	const formas = conta_entrada.map((parcela) => parcela.forma).filter(Boolean);

	return {
		id: venda.id_venda,
		data: venda.datavenda,
		situacao: venda.ativo === "SIM" ? "Ativa" : "Cancelada",
		pagamento: [...new Set(formas)].join(", "),
		cliente: venda.nome,
		dest_cpfcnpj: venda.cpf,
		itens: linhasDaVenda(venda_item, venda_complementos),
		total: venda.valorvenda,
	};
}

async function carregarXml(documento) {
	const { data } = await reqNotaFiscal.xml(documento.id_nota_fiscal);
	return data;
}

async function carregarVenda(documento) {
	if (!documento.id_venda) return null;

	const { data } = await reqVenda.listVendaCompleta(documento.id_venda);
	return data?.venda ? vendaDe(data) : null;
}

export const fonteDocumentos = {
	documento: documentoDe,
	xml: carregarXml,
	venda: carregarVenda,
};

export async function listarDocumentos(f) {
	const { data } = await reqNotaFiscal.listRelatorio({
		dtini: functions.dateByString(f.dtIni),
		dtfim: functions.dateByString(f.dtFim),
		modelo: f.modelo,
		status: f.status,
		venda: f.venda,
		cliente: f.cliente,
		cpfcnpj: LibMask.onlynumbers(f.cpfCnpj),
	});

	return data || [];
}

export async function listarPendencias(f) {
	const { data } = await reqNotaFiscal.pendencias({
		dtini: functions.dateByString(f.dtIni),
		dtfim: functions.dateByString(f.dtFim),
		modelo: f.modelo,
		venda: f.venda,
		cliente: f.cliente,
		cpfcnpj: LibMask.onlynumbers(f.cpfCnpj),
	});

	return data || [];
}

export async function imprimirNota(documento) {
	const { data } = await reqNotaFiscal.pdf(documento.id_nota_fiscal);
	const pdf = new Blob([bytesDeBase64(data.arquivo)], { type: "application/pdf" });
	const aba = window.open(URL.createObjectURL(pdf), "_blank");

	if (!aba) saveAs(pdf, data.nome_arquivo);
}

export function erroDaSefaz(erro) {
	const { cstat, motivo } = erro?.message ?? {};
	return motivo ? new Error([cstat, motivo].filter(Boolean).join(" · ")) : erro;
}

export async function cancelarNota(documento, justificativa) {
	try {
		await reqNotaFiscal.cancelar(documento.id_nota_fiscal, justificativa);
	} catch (erro) {
		throw erroDaSefaz(erro);
	}
}

export async function transmitirNota(documento) {
	try {
		await reqNotaFiscal.transmitir(documento.id_nota_fiscal);
	} catch (erro) {
		throw erroDaSefaz(erro);
	}
}

export async function consultarNota(documento) {
	try {
		const { data } = await reqNotaFiscal.consultar(documento.id_nota_fiscal);
		return data;
	} catch (erro) {
		if (erro?.message?.cstat) return erro.message;
		throw erroDaSefaz(erro);
	}
}

export function salvarXml(conteudo, nome) {
	saveAs(new Blob([conteudo], { type: "application/xml" }), nome);
}

export async function baixarXml(documento) {
	const { xml, xml_evento } = documento.xml ? documento : await carregarXml(documento);

	salvarXml(xml, `${documento.chave}.xml`);
	if (xml_evento) salvarXml(xml_evento, `${documento.chave}-procEventoNFe.xml`);
}
